import React from 'react';
import PropTypes from 'prop-types';
import block from 'bem-cn-lite';
import _ from 'lodash';
import {compose} from 'recompose';
import {Icon, Spin} from '@yandex-data-ui/common';
import {HTML} from '@doc-tools/components';
import {FormSpy} from 'react-final-form';
import arrayMutators from 'final-form-arrays';

import axios from 'axiosWrapper';
import {roundNumber} from 'utils';
import {extractTrafficData} from 'components/calculator/Traffic/utils';

import withLocale from 'hoc/withLocale';
import withTranslation from 'hoc/withTranslation';
import Receipt from 'components/calculator/Receipt/Receipt';
import DelayRender from 'components/DelayRender/DelayRender';
import CloudTag from 'components/tags/CloudTag/CloudTag';
import CalculatorFormError from '../CalculatorFormError/CalculatorFormError';
import {ServiceIcon} from 'components/ServiceIcon';
import FinalForm from 'components/FinalForm/FinalForm';
import {calculate} from 'units/calculator/api';
import {getConfigsMap, getServicesConfigs, getAllServicesConfigs, PeriodInterval} from '../config';
import Banner from '../Banner/Banner';

import plusIcon from '@yandex-data-ui/common/assets/icons/plus.svg';
import supportIcon from 'icons/support.svg';
import closeIcon from 'icons/close.svg';

import './PricesCalculator.scss';

const b = block('PricesCalculator');
const SUPPORT = 'support';
const ICON_SIZE = 16;

export const CALCULALTOR_QA_PREFIX = 'calculator';

class PricesCalculator extends React.Component {
    static getDerivedStateFromError() {
        return {hasError: true};
    }

    static propTypes = {
        initialService: PropTypes.string,
        services: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
        customOsProductId: PropTypes.string,
        className: PropTypes.string,
        locale: PropTypes.object,
        i18nK: PropTypes.func,
    };

    constructor(props) {
        super(props);
        this.forms = {};
        this.cancelSources = {};
        this.state = {
            hasError: false,
            isMounted: false,
            period: 'month',
            receipts: {},
            receiptsLoading: false,
            receiptsError: null,
            services: [this.props.initialService],
            currentService: this.props.initialService,
        };
    }

    componentDidMount() {
        this.fetchConfigs();
    }

    safeFormRef = (service, ref) => {
        this.forms[service] = ref;
    };

    cancelRunningRequests(services) {
        services.forEach((id) => {
            if (this.cancelSources[id]) {
                this.cancelSources[id].cancel();
            }
        });
    }

    async receiptRequest(service, data, period) {
        const {locale} = this.props;
        const interval = period && PeriodInterval[period];
        const cancelToken = this.cancelSources[service].token;
        return calculate(locale, service, data, interval, cancelToken);
    }

    async supportReceiptRequest(data, period) {
        const {locale, i18nK} = this.props;
        const {template} = data;

        if (template.includes('premium')) {
            return null;
        }

        const cancelToken = this.cancelSources[SUPPORT].token;
        const {items} = await calculate(locale, 'support', data, period, cancelToken);

        if (!items.length) {
            return {items: []};
        }

        return {
            items: [
                {
                    ...items[0],
                    name: `${i18nK('label_receipt-support')} ${i18nK(template)}`,
                },
            ],
        };
    }

    async fetchConfigs() {
        const {services, initialService, locale} = this.props;

        this.configs = services
            ? getServicesConfigs(Array.isArray(services) ? services : [services])
            : await getAllServicesConfigs(locale?.code);

        this.configsMap = getConfigsMap(this.configs);
        this.isOneService = this.configs.length === 1;
        const hasServices = this.configs.length > 0;
        const defaultService = hasServices ? this.configs[0].id : undefined;
        const currentService = initialService || defaultService;
        this.setState({
            isMounted: hasServices,
            currentService,
            services: currentService ? [currentService] : [],
        });
    }

    async fetchReceipt(service, data, period) {
        this.cancelSources[service] = axios.CancelToken.source();

        const result =
            service === SUPPORT
                ? await this.supportReceiptRequest(data, period)
                : await this.receiptRequest(service, data, period);

        return result && result.items;
    }

    async fetchTrafficReceipt() {
        const {services, period} = this.state;
        const data = services.map((id) => this.forms[id].getState().values);
        const trafficData = extractTrafficData(services, data);

        if (_.isEmpty(trafficData)) {
            this.setState({trafficReceipt: undefined});
        } else {
            const trafficReceiptItems = await this.fetchReceipt('traffic', trafficData, period);

            if (trafficReceiptItems && trafficReceiptItems.length) {
                const trafficReceipt = trafficReceiptItems
                    .map((receipt) => ({...receipt, nonClosable: true}))
                    .sort(({name: name1}, {name: name2}) => (name1 > name2 ? -1 : 1));
                this.setState({trafficReceipt});
            }
        }
    }

    async fetchReceipts(services, data, period) {
        this.cancelRunningRequests(services);
        this.setState({receiptsLoading: true});

        try {
            // eslint-disable-next-line no-unused-vars
            const [_, ...receipts] = await Promise.all([
                this.fetchTrafficReceipt(),
                ...services.map((id, index) => this.fetchReceipt(id, data[index], period)),
            ]);

            this.updateReceipt(services, receipts.filter(Boolean));
            this.setState({receiptsLoading: false, receiptsError: null});
        } catch (err) {
            if (axios.isCancel(err)) {
                return;
            }

            this.setState({receiptsLoading: false, receiptsError: {services}});
        }
    }

    updateReceipt(services, data) {
        this.setState((prevState) => ({
            receipts: services.reduce(
                (acc, id, index) => {
                    acc[id] = data[index];
                    return acc;
                },
                {...prevState.receipts},
            ),
        }));
    }

    addForm(service) {
        this.setState((prevState) => ({
            services: [...prevState.services, service],
            currentService: service,
        }));
    }

    removeForm(service) {
        this.setState((prevState) => {
            const newServices = prevState.services.filter((id) => id !== service);

            return {
                services: newServices,
                currentService:
                    prevState.currentService === service
                        ? newServices[0]
                        : prevState.currentService,
                receipts: _.omit(prevState.receipts, service),
            };
        }, this.fetchTrafficReceipt);
    }

    selectForm(service) {
        this.setState({
            currentService: service,
        });
    }

    prepareReceiptItem(service, itemNameKey) {
        const {i18nK} = this.props;

        const data = this.state.receipts[service];
        const config = this.configsMap[service];

        if (!data) {
            return null;
        }

        const mainName = i18nK(itemNameKey);
        const mainCost = roundNumber(
            data.reduce((acc, {cost}) => acc + cost, 0),
            2,
            4,
        );

        return {
            id: service,
            multi: config.multi,
            name: mainName,
            cost: mainCost,
            items: data.map(({name, cost}) => ({name, cost})),
        };
    }

    handleFormChange = _.debounce(async (service, data) => {
        const {period} = this.state;

        if (_.isEmpty(data)) {
            return;
        }

        this.fetchReceipts([service], [data], period);
    }, 200);

    handleReceiptPeriodChange = (period) => {
        const {services} = this.state;
        const data = services.map((id) => this.forms[id].getState().values);

        this.setState({period});
        this.fetchReceipts(services, data, period);
    };

    handleServiceItemClose(event, service) {
        event.stopPropagation();
        this.removeForm(service);
    }

    handleReceiptItemClose = (service) => {
        this.removeForm(service);
    };

    handleReceiptErrorActionClick = () => {
        const {services, period} = this.state;
        const data = services.map((id) => this.forms[id].getState().values);

        this.fetchReceipts(services, data, period);
    };

    handleServiceClick = (service) => {
        const {services} = this.state;

        if (services.includes(service)) {
            this.selectForm(service);
        } else {
            this.addForm(service);
        }
    };

    getServiceTitle(service) {
        const {i18nK} = this.props;
        const {id} = this.configsMap[service];
        return i18nK(`${id}-form-title`);
    }

    renderServiceIcon(serviceId) {
        return (
            <div className={b('service-icon-main')}>
                {serviceId === SUPPORT ? (
                    <Icon data={supportIcon} size={ICON_SIZE} />
                ) : (
                    <ServiceIcon name={serviceId} size={ICON_SIZE} />
                )}
            </div>
        );
    }
    renderServices() {
        const {i18nK} = this.props;
        const {services, currentService} = this.state;

        if (!this.configs || this.isOneService) {
            return null;
        }

        return (
            <div className={b('service-tags')}>
                {this.configs.map(({id, nameKey}) => (
                    <CloudTag
                        key={id}
                        active={services.includes(id)}
                        className={b('service-tag', {
                            selected: services.includes(id),
                            current: id === currentService,
                        })}
                        onClick={this.handleServiceClick.bind(this, id)}
                        theme="calculator"
                        size="n"
                        dataQa={`${CALCULALTOR_QA_PREFIX}-${id}-tag`}
                    >
                        <div className={b('service-tag-logo')}>
                            <div className={b('service-tag-icon-main')}>
                                {this.renderServiceIcon(id)}
                            </div>
                            <div className={b('service-tag-icon-add')}>
                                <Icon data={plusIcon} size={ICON_SIZE} />
                            </div>
                        </div>
                        <div className={b('service-tag-name')}>
                            <HTML>{i18nK(nameKey)}</HTML>
                        </div>
                        <div className={b('service-tag-close')}>
                            <Icon
                                onClick={(e) => this.handleServiceItemClose(e, id)}
                                className={b('service-tag-icon-close')}
                                data={closeIcon}
                                size={10}
                            />
                        </div>
                    </CloudTag>
                ))}
            </div>
        );
    }

    renderFormError() {
        const {currentService} = this.state;

        return (
            <CalculatorFormError
                title={this.getServiceTitle(currentService)}
                action={{
                    handler: () => {
                        this.removeForm(currentService);
                        this.setState({hasError: false});
                    },
                }}
            />
        );
    }

    renderForms() {
        const {locale, i18nK} = this.props;
        const {services, currentService, hasError} = this.state;

        if (hasError) {
            return this.renderFormError();
        }

        if (services.length === 0) {
            return (
                <div className={b('empty')}>
                    <div className={b('empty-title')}>{i18nK('label_empty-title')}</div>
                    <div className={b('empty-description')}>{i18nK('label_empty-description')}</div>
                </div>
            );
        }

        return services.map((service) => {
            const {customOsProductId} = this.props;
            const isCompute = service === 'compute';
            const current = service === currentService;
            const config = this.configsMap[service];
            const {form: Form} = config;

            const formElement = (
                <FinalForm
                    formSettings={{onSubmit: () => {}, mutators: {...arrayMutators}}}
                    onChange={this.handleFormChange.bind(this, service)}
                    ref={this.safeFormRef.bind(this, service)}
                >
                    <FormSpy
                        // This work like 'onInitialize'
                        subscription={{initialValues: true}}
                        onChange={(formState) => {
                            this.handleFormChange(service, formState.initialValues);
                        }}
                    />
                    <Form
                        title={this.getServiceTitle(service)}
                        locale={locale}
                        service={service}
                        customOsProductId={isCompute && customOsProductId}
                    />
                </FinalForm>
            );

            return (
                <div
                    key={service}
                    className={b('form', {current})}
                    data-qa={`${CALCULALTOR_QA_PREFIX}-${service}-form`}
                >
                    {formElement}
                </div>
            );
        });
    }

    renderReceipt() {
        const {customOsProductId, i18nK} = this.props;
        const {services, period, receiptsError, trafficReceipt} = this.state;
        let items = services
            .map((id) => this.prepareReceiptItem(id, this.configsMap[id].nameKey))
            .filter(Boolean);

        if (trafficReceipt) {
            items = [...items, ...trafficReceipt];
        }

        return (
            <div className={b('receipt')} data-qa={`${CALCULALTOR_QA_PREFIX}-receipt`}>
                <Receipt
                    title={i18nK('label_receipt-title')}
                    items={items}
                    period={period}
                    error={receiptsError}
                    onPeriodChange={this.handleReceiptPeriodChange}
                    onItemClose={this.handleReceiptItemClose}
                    onErrorActionClick={this.handleReceiptErrorActionClick}
                    isOneService={this.isOneService}
                    customOsProductId={customOsProductId}
                />
            </div>
        );
    }

    renderReceiptLoader() {
        const {receiptsLoading} = this.state;

        if (!receiptsLoading) {
            return null;
        }

        return (
            <DelayRender>
                {() => (
                    <div className={b('receipt-loader')}>
                        <Spin size="xl" />
                    </div>
                )}
            </DelayRender>
        );
    }

    render() {
        const {i18nK, className} = this.props;
        const {isMounted} = this.state;

        if (!isMounted) {
            return null;
        }

        return (
            <div className={b(null, className)} data-qa={CALCULALTOR_QA_PREFIX}>
                <div className={b('header')}>
                    <div className={b('title')}>{i18nK('label_title')}</div>
                    {/* <div className={b('disclaimer')}>{i18nK('label_disclaimer')}</div> */}
                </div>
                <div className={b('sub-header')}>{this.renderServices()}</div>
                <div className={b('content')}>
                    <div className={b('left')}>
                        <div className={b('left-top')}>
                            {this.renderForms()}
                            {this.isOneService && (
                                <div className={b('note')}>
                                    <HTML>{i18nK('label_note')}</HTML>
                                </div>
                            )}
                        </div>
                        <div className={b('left-bottom')}>
                            {!this.isOneService && (
                                <div className={b('note')}>
                                    <HTML>{i18nK('label_note')}</HTML>
                                </div>
                            )}
                            <Banner />
                        </div>
                    </div>
                    <div className={b('separator')} />
                    <div className={b('right')}>
                        {this.renderReceipt()}
                        {this.renderReceiptLoader()}
                    </div>
                </div>
            </div>
        );
    }
}

export default compose(withLocale, withTranslation('calculator'))(PricesCalculator);
